"use client";

import { useEffect, useId, useMemo, useRef, useState } from "react";
import { ChevronDownIcon, CheckIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { cn } from "@/lib/utils";
import {
  groupBranchesByRegion,
  sortBranchesByDistance,
} from "@/lib/utils/branch-groups";
import { useGrantedGeolocation } from "@/hooks/useGrantedGeolocation";
import type { Branch } from "@/types";

/** "가까운 지점" 그룹에 올릴 최대 개수. */
const NEARBY_LIMIT = 5;

/** 지점이 아닌 수령/반납 방법(숙소 수령·국내 배송) 항목. */
export interface BranchComboboxMethodOption {
  value: string;
  label: string;
}

interface BranchComboboxProps {
  value: string;
  onValueChange: (value: string) => void;
  placeholder: string;
  /** 이미 location_type / branch_type 으로 걸러진 지점 목록. */
  branches: Branch[];
  /** 지점 그룹 전체를 감싸는 라벨(= 기존 Select 의 SelectLabel). */
  branchesLabel: string;
  /** 지점 목록 뒤에 붙는 비-지점 항목들. */
  methodOptions: BranchComboboxMethodOption[];
  disabled?: boolean;
}

type Row =
  | { kind: "header"; id: string; label: string; count?: number }
  | { kind: "option"; id: string; value: string; label: string; hint?: string };

/**
 * 지점 수가 많은 상품(최대 122지점 / 60도시)을 위한 검색형 지점 선택기.
 *
 * shadcn `Command` 컴포넌트가 이 저장소에 없어서(cmdk 미설치) Popover +
 * 검색 input + 수동 키보드 내비게이션으로 같은 패턴을 구현한다.
 *
 * 목록 구성(위→아래)
 *  1. 선택한 지점 — 현재 값이 필터를 통과하면 항상 맨 위에 고정
 *  2. 가까운 지점 — 위치 권한이 *이미* 허용된 경우에만 (프롬프트 없음)
 *  3. 시/도 그룹 — 그룹명 + 지점 수
 *  4. 숙소 수령 / 국내 배송 등 비-지점 항목
 *
 * 1·2번은 바로가기 성격이라 3번 그룹에서 제거하지 않는다 — 그룹 헤더의 개수가
 * 실제 지점 수와 어긋나지 않게 하기 위함.
 */
export default function BranchCombobox({
  value,
  onValueChange,
  placeholder,
  branches,
  branchesLabel,
  methodOptions,
  disabled,
}: BranchComboboxProps) {
  const t = useTranslations("product.booking");
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const listId = useId();
  const listRef = useRef<HTMLDivElement>(null);

  // 권한이 이미 granted 일 때만 좌표를 얻는다. 열려 있을 때만 조회.
  const coords = useGrantedGeolocation(open);

  const selectedLabel = useMemo(() => {
    const method = methodOptions.find((m) => m.value === value);
    if (method) return method.label;
    return branches.find((b) => b.id === value)?.name ?? "";
  }, [value, branches, methodOptions]);

  const rows = useMemo<Row[]>(() => {
    const matchedBranches = branches;
    const matchedMethods = methodOptions;

    const result: Row[] = [];

    // 1. 선택한 항목 고정
    const selectedBranch = matchedBranches.find((b) => b.id === value);
    const selectedMethod = matchedMethods.find((m) => m.value === value);
    if (selectedBranch || selectedMethod) {
      result.push({
        kind: "header",
        id: "h-selected",
        label: t("selectedLocation"),
      });
      result.push({
        kind: "option",
        id: `selected-${value}`,
        value,
        label: selectedBranch ? selectedBranch.name : selectedMethod!.label,
        hint: selectedBranch?.city ?? undefined,
      });
    }

    // 2. 가까운 지점 (권한 허용 + 검색어 없음)
    if (coords && matchedBranches.length > NEARBY_LIMIT) {
      const nearby = sortBranchesByDistance(
        matchedBranches.filter(
          (b) => b.latitude != null && b.longitude != null,
        ),
        coords,
      ).slice(0, NEARBY_LIMIT);
      if (nearby.length > 0) {
        result.push({
          kind: "header",
          id: "h-nearby",
          label: t("nearbyBranches"),
        });
        for (const branch of nearby) {
          result.push({
            kind: "option",
            id: `nearby-${branch.id}`,
            value: branch.id,
            label: branch.name,
            hint: branch.city ?? undefined,
          });
        }
      }
    }

    // 3. 시/도 그룹
    const groups = groupBranchesByRegion(matchedBranches, branchesLabel);
    for (const group of groups) {
      result.push({
        kind: "header",
        id: `h-${group.key}`,
        label: group.label,
        count: group.branches.length,
      });
      for (const branch of group.branches) {
        result.push({
          kind: "option",
          id: `b-${branch.id}`,
          value: branch.id,
          label: branch.name,
        });
      }
    }

    // 4. 비-지점 항목
    for (const method of matchedMethods) {
      result.push({
        kind: "option",
        id: `m-${method.value}`,
        value: method.value,
        label: method.label,
      });
    }

    return result;
  }, [branches, methodOptions, value, coords, branchesLabel, t]);

  const optionIndexes = useMemo(
    () =>
      rows.reduce<number[]>((acc, row, index) => {
        if (row.kind === "option") acc.push(index);
        return acc;
      }, []),
    [rows],
  );

  const activeRowIndex = optionIndexes.includes(activeIndex)
    ? activeIndex
    : (optionIndexes[0] ?? -1);

  // 활성 항목을 보이는 영역으로 — DOM 부수효과만, setState 없음.
  useEffect(() => {
    if (!open || activeRowIndex < 0) return;
    const node = listRef.current?.querySelector<HTMLElement>(
      `[data-row-index="${activeRowIndex}"]`,
    );
    node?.scrollIntoView({ block: "nearest" });
  }, [open, activeRowIndex]);

  const moveActive = (direction: 1 | -1) => {
    if (optionIndexes.length === 0) return;
    const current = optionIndexes.indexOf(activeRowIndex);
    const next =
      current < 0
        ? 0
        : (current + direction + optionIndexes.length) % optionIndexes.length;
    setActiveIndex(optionIndexes[next]);
  };

  const commit = (nextValue: string) => {
    onValueChange(nextValue);
    setOpen(false);
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (next) setActiveIndex(-1);
  };

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={handleOpenChange} modal={false}>
      <PopoverPrimitive.Trigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          disabled={disabled}
          className={cn(
            "border-input flex h-9 w-full items-center justify-between gap-2 rounded-md border bg-transparent px-3 py-2 text-sm shadow-xs transition-[color,box-shadow] outline-none",
            "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
            "hover:bg-accent hover:text-accent-foreground cursor-pointer",
            "disabled:cursor-not-allowed disabled:opacity-50",
          )}
        >
          <span
            className={cn(
              "line-clamp-1 text-left",
              !selectedLabel && "text-muted-foreground",
            )}
          >
            {selectedLabel || placeholder}
          </span>
          <ChevronDownIcon className="size-4 shrink-0 opacity-50" />
        </button>
      </PopoverPrimitive.Trigger>
      {/* Portal 없이 트리거 옆에 그린다(shadcn PopoverContent 는 항상 Portal).
          모바일 예약 카드는 vaul 드로어 안에 있고, 드로어는 열린 동안 body 에
          pointer-events:none 을 걸어 바깥으로 나간 팝오버의 터치 스크롤을 막았다
          (데스크톱은 드로어가 없어 정상) — 09-22. */}
      <PopoverPrimitive.Content
        align="start"
        sideOffset={4}
        collisionPadding={8}
        className="z-50 w-(--radix-popover-trigger-width) max-w-[calc(100vw-2rem)] overflow-hidden rounded-md border bg-popover p-0 text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2 origin-[--radix-popover-content-transform-origin]"
        onOpenAutoFocus={(event) => {
          // 트리거로 포커스가 돌아가며 스크롤이 튀는 걸 막는다. 검색 칸은 없고
          // (09-22 제거: 인풋 포커스가 모바일 키보드를 띄워 목록 스크롤을 막았다)
          // 키보드 탐색은 목록 컨테이너가 받는다.
          event.preventDefault();
          listRef.current?.focus({ preventScroll: true });
        }}
      >
        {/* data-vaul-no-drag: 모바일 예약 드로어(vaul) 안에서 이 목록의 터치 이동이
            드로어 드래그로 먹히지 않게(검색 필터 시트와 같은 처리). touch-action 으로
            세로 팬만 이 목록이 처리한다. */}
        <div
          ref={listRef}
          id={listId}
          role="listbox"
          tabIndex={-1}
          onKeyDown={(event) => {
            if (event.key === "ArrowDown") {
              event.preventDefault();
              moveActive(1);
            } else if (event.key === "ArrowUp") {
              event.preventDefault();
              moveActive(-1);
            } else if (event.key === "Enter") {
              const row = rows[activeRowIndex];
              if (row && row.kind === "option") {
                event.preventDefault();
                commit(row.value);
              }
            }
          }}
          data-vaul-no-drag
          style={{ touchAction: "pan-y" }}
          className="max-h-72 overflow-y-auto overscroll-contain"
        >
          {rows.length === 0 && (
            <p className="px-3 py-6 text-center text-sm text-muted-foreground">
              {t("noBranchResults")}
            </p>
          )}
          {rows.map((row, index) =>
            row.kind === "header" ? (
              // 그룹 헤더 — 회색 밴드로 항목과 확실히 가른다(09-22). sticky 라
              // 스크롤 중에도 어느 묶음인지 보인다.
              <div
                key={row.id}
                className="flex h-8 items-center justify-between gap-2 border-b border-gray-100 bg-gray-50 px-3 text-[11px] font-semibold uppercase tracking-wide text-gray-500"
              >
                <span className="truncate">{row.label}</span>
                {row.count != null && (
                  <span className="shrink-0 tabular-nums">{row.count}</span>
                )}
              </div>
            ) : (
              <button
                key={row.id}
                type="button"
                role="option"
                aria-selected={row.value === value}
                data-row-index={index}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => commit(row.value)}
                className={cn(
                  "flex h-11 w-full items-center gap-2 px-3 text-left text-sm",
                  index === activeRowIndex && "bg-accent",
                )}
              >
                <span className="min-w-0 flex-1 truncate">{row.label}</span>
                {row.hint && (
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {row.hint}
                  </span>
                )}
                {row.value === value && (
                  <CheckIcon className="size-4 shrink-0 opacity-60" />
                )}
              </button>
            ),
          )}
        </div>
      </PopoverPrimitive.Content>
    </PopoverPrimitive.Root>
  );
}
